import {RelativeTime} from '@primer/react'
import {formatDuration} from '../../../../utils/format-duration'
import type {AhpToolTiming} from '../../../../ahp/use-ahp-tool-timing'
import {parseContent} from '../../tool/ToolPrimitives'
import styles from './AhpToolCallFooter.module.css'

const BYTE_UNITS = ['B', 'KB', 'MB', 'GB']
const encoder = new TextEncoder()

export function formatBytes(bytes: number): string {
  let value = bytes
  let unit = 0
  while (value >= 1024 && unit < BYTE_UNITS.length - 1) {
    value /= 1024
    unit++
  }
  return unit === 0 ? `${value} ${BYTE_UNITS[0]}` : `${value.toFixed(1)} ${BYTE_UNITS[unit]}`
}

export function utf8ByteLength(text: string): number {
  return encoder.encode(text).length
}

type FooterProps = {
  timing: AhpToolTiming | undefined
  settled: boolean
  output: string | undefined
}

function footerFields({timing, settled, output}: FooterProps) {
  const startedAt = timing?.startedAt
  const durationMs = timing?.durationMs
  const elapsedFrom = timing?.elapsedFrom
  const hasDuration = durationMs !== undefined || elapsedFrom !== undefined
  const text = parseContent(output).content
  // Empty text on a settled call is a known 0 B; in-flight calls show size once output arrives.
  const outputBytes = text !== undefined && (settled || text !== '') ? utf8ByteLength(text) : undefined
  return {startedAt, durationMs, elapsedFrom, hasDuration, outputBytes}
}

export function hasAhpToolCallFooterContent(props: FooterProps): boolean {
  const {startedAt, hasDuration, outputBytes} = footerFields(props)
  return Boolean(startedAt) || hasDuration || outputBytes !== undefined
}

export function AhpToolCallFooter(props: FooterProps) {
  const {startedAt, durationMs, elapsedFrom, hasDuration, outputBytes} = footerFields(props)
  if (!startedAt && !hasDuration && outputBytes === undefined) return null

  return (
    <dl className={styles.footer} data-testid="ahp-tool-call-footer">
      {startedAt && (
        <div className={styles.item}>
          <dt className={styles.label}>Started</dt>
          <dd className={styles.value}>
            <RelativeTime
              datetime={startedAt}
              format="datetime"
              month="short"
              day="numeric"
              year="numeric"
              hour="numeric"
              minute="2-digit"
            />
          </dd>
        </div>
      )}
      {hasDuration && (
        <div className={styles.item}>
          <dt className={styles.label}>Duration</dt>
          <dd className={styles.value}>
            {durationMs !== undefined ? (
              formatDuration(durationMs)
            ) : (
              <RelativeTime noTitle format="elapsed" datetime={elapsedFrom} />
            )}
          </dd>
        </div>
      )}
      {outputBytes !== undefined && (
        <div className={styles.item}>
          <dt className={styles.label}>Output</dt>
          <dd className={styles.value}>{formatBytes(outputBytes)}</dd>
        </div>
      )}
    </dl>
  )
}
